import { Blueprint } from '../../blueprint.class';
import { connectIslands } from '../../../../helpers/connect-islands';
import { emptyIslandDefaults, EmptyIslandDefaults } from '../../../../helpers/empty-island-defaults';
import { GALLEY_WALKWAY_PADDING } from '../../../../constants';
import { Island } from '../../../islands/island.class';
import { newEmptyIsland } from '../../../../helpers/new-empty-island';
import { ToolResult } from '../../types';

export type AddIslandSide = 'left' | 'right' | 'parallel';
export type AddIslandType = 'grill' | 'bar' | 'corner' | 'smoker' | 'pizza' | 'asado';

export type AddIslandArgs = {
	islandType: AddIslandType;
	side: AddIslandSide;
};

const VALID_TYPES: ReadonlyArray<AddIslandType> = ['grill', 'bar', 'corner', 'smoker', 'pizza', 'asado'];
const VALID_SIDES: ReadonlyArray<AddIslandSide> = ['left', 'right', 'parallel'];

const STRAIGHT_ANGLE = 180;

// islands are meant to butt up against one another, so a shared edge is not a collision;
// only a real interpenetration of the walkway counts
const OVERLAP_TOLERANCE = 1e-6;

// a world-space bounding box on the floor plane: x runs left to right, z runs back to front
type Footprint = { maxX: number, maxZ: number, minX: number, minZ: number };

const wrapAngle = (angle: number): number => ((angle % 360) + 360) % 360;

const roundInches = (value: number): number => Math.round(value * 10) / 10;

/**
 * The island's footprint in world space, taken from its own rotated panel corners -- the same
 * measurement getSizeCfn uses to bound a kitchen. Countertop overhangs are deliberately excluded:
 * `padding` measures the clear gap between two BASE front faces, so the walkway must be measured
 * the same way or a bar's 10" overhang would fail an otherwise correct pairing.
 */
const getFootprint = (island: Island): Footprint => {
	const { back, front, left, right } = island.getCenterOffsets(island.axis.y, false);

	return {
		maxX: island.origin.x + right,
		maxZ: island.origin.z + back,
		minX: island.origin.x + left,
		minZ: island.origin.z + front
	};
};

/**
 * The strip a parallel island needs to itself: the walkway the customer walks through, plus the
 * ground the facing island lands on. The anchor is forced to axis 0 before this runs, so the strip
 * always opens along +z, centred on the anchor (front<->front connections align on centre).
 */
const getWalkwayZone = (anchor: Island, islandType: AddIslandType): Footprint => {
	const { depth, width } = emptyIslandDefaults[islandType];
	// only a corner island carries a { left, right } depth, and a corner is refused before this
	const newDepth = typeof depth === 'number' ? depth : Math.max(depth.left, depth.right);
	const anchorFront = anchor.origin.z + anchor.getCenterOffsets(0, false).back;

	return {
		maxX: anchor.origin.x + (width / 2),
		maxZ: anchorFront + GALLEY_WALKWAY_PADDING + newDepth,
		minX: anchor.origin.x - (width / 2),
		minZ: anchorFront
	};
};

const overlaps = (a: Footprint, b: Footprint): boolean => {
	const sharedX = Math.min(a.maxX, b.maxX) - Math.max(a.minX, b.minX);
	const sharedZ = Math.min(a.maxZ, b.maxZ) - Math.max(a.minZ, b.minZ);

	return sharedX > OVERLAP_TOLERANCE && sharedZ > OVERLAP_TOLERANCE;
};

const computeNewAngle = (islandType: AddIslandType, side: AddIslandSide, anchorAngle: number): number => {
	// 'parallel' is tested first on purpose: a corner's +/-45 must never compose with the +180,
	// which would leave the facing island cocked across the walkway instead of squared up to it.
	if (side === 'parallel') {
		return wrapAngle(anchorAngle + STRAIGHT_ANGLE);
	}
	if (islandType === 'corner') {
		return wrapAngle(side === 'right' ? anchorAngle + 45 : anchorAngle - 45);
	}
	return wrapAngle(anchorAngle);
};

const getArgumentRefusal = ({ islandType, side }: AddIslandArgs): string | null => {
	if (!islandType) return `Missing islandType. Valid values: ${VALID_TYPES.join(', ')}.`;
	if (!VALID_TYPES.includes(islandType)) return `Invalid islandType '${islandType}'. Valid values: ${VALID_TYPES.join(', ')}.`;
	if (!side) return `Missing side. Valid values: ${VALID_SIDES.join(', ')}.`;
	if (!VALID_SIDES.includes(side)) return `Invalid side '${side}'. Valid values: ${VALID_SIDES.join(', ')}.`;

	// a corner is a 45deg turn in the run, so it has no square front face to put across a walkway
	if (islandType === 'corner' && side === 'parallel') {
		return 'A corner island cannot face another island. Pick a bar, grill, smoker, pizza or asado island.';
	}

	return null;
};

const getParallelRefusal = (siblings: Array<Island>, anchor: Island, islandType: AddIslandType): string | null => {
	const anchorAngle = wrapAngle(anchor.axis.y);

	// the new island is placed at anchorAngle + 180, so a squared-up walkway only exists
	// when the anchor itself faces forward; off any other angle the two runs are skewed
	if (anchorAngle !== 0) {
		return `Island '${anchor.id}' is angled at ${anchorAngle}deg, so nothing can face it squarely. A parallel island only works off a forward-facing island.`;
	}

	// Everything already standing in the strip the facing island needs. Two ways in:
	//   1. a SECOND facing island computes an identical origin to the first -- same target panel,
	//      same centre alignment, same padding -- and interpenetrates it silently, because
	//      newConnection only dedups on the (panelId, islandId) pair and a new island always
	//      brings a new islandId;
	//   2. an L- or U-shape side run sits at axis 90/270, so its WIDTH runs along z and it reaches
	//      forward past the anchor's front face, straight through the walkway. At the seeded 84" it
	//      leaves a 4" slot instead of the 60" the success message promises; past 88" it lands
	//      inside the facing island itself, and resize_island reaches 144".
	const zone = getWalkwayZone(anchor, islandType);
	const blockers = siblings
		.filter((island) => island.id !== anchor.id)
		.map((island) => ({ footprint: getFootprint(island), island }))
		.filter(({ footprint }) => overlaps(footprint, zone))
		.sort((a, b) => b.footprint.maxZ - a.footprint.maxZ);

	const facing = blockers.find(({ island }) => wrapAngle(island.axis.y) === wrapAngle(anchorAngle + STRAIGHT_ANGLE)) ?? null;
	if (facing) {
		return `Island '${facing.island.id}' already faces island '${anchor.id}' across the walkway. Resize or replace that island instead of adding a second facing one.`;
	}

	// name the deepest intruder: it is the one that has to give way the furthest
	const [blocker] = blockers;
	if (blocker) {
		const past = roundInches(blocker.footprint.maxZ - zone.minZ);

		return `Island '${blocker.island.id}' runs ${past} inches past the front of island '${anchor.id}', through the ` +
			`${GALLEY_WALKWAY_PADDING}-inch walkway a facing island needs. Shorten or remove that island first, or change the kitchen shape instead.`;
	}

	return null;
};

const getFrontRowIslands = (islands: Array<Island>): Array<Island> => {
	const frontRow = islands.filter((island) => island.axis.y !== 180);
	if (frontRow.length === 0) return [];

	const leftmost = frontRow.reduce((left, island) => (island.origin.x < left.origin.x ? island : left), frontRow[0]);
	const ordered: Array<Island> = [];
	const visited = new Set<string>();

	const traverse = (island: Island): void => {
		if (visited.has(island.id)) return;
		visited.add(island.id);
		ordered.push(island);

		island.connections.children
			.filter((c) => !c.isSource && c.targetIsland && !visited.has(c.targetIsland.id))
			.map((c) => c.targetIsland as Island)
			.filter((target) => target.axis.y !== 180 && frontRow.some((fr) => fr.id === target.id))
			.sort((a, b) => a.origin.x - b.origin.x)
			.forEach((target) => traverse(target));
	};

	traverse(leftmost);

	frontRow
		.filter((island) => !visited.has(island.id))
		.sort((a, b) => a.origin.x - b.origin.x)
		.forEach((island) => ordered.push(island));

	return ordered;
};

export const addIslandTool = (blueprint: Blueprint, { islandType, side }: AddIslandArgs): ToolResult<{ islandId: string }> => {
	const argumentRefusal = getArgumentRefusal({ islandType, side });
	if (argumentRefusal) return { success: false, error: argumentRefusal };

	const kitchen = blueprint.kitchens.getActiveVersion();
	if (!kitchen) return { success: false, error: 'No active kitchen found.' };

	const { islands } = kitchen.design;

	const frontRow = getFrontRowIslands(islands.children);
	if (frontRow.length === 0) {
		return { success: false, error: 'No front-row island found to attach to. The kitchen has no islands or only galley islands.' };
	}

	// a parallel island faces the kitchen rather than extending it, so it anchors on the DEFAULT island --
	// the one every other origin is measured from -- not on whichever island happens to end the run
	const anchor = side === 'parallel' ? (islands.getDefaultIsland() ?? frontRow[0]) : (side === 'right' ? frontRow[frontRow.length - 1] : frontRow[0]);

	const parallelRefusal = side === 'parallel' ? getParallelRefusal(islands.children, anchor, islandType) : null;
	if (parallelRefusal) return { success: false, error: parallelRefusal };

	const newAngle = computeNewAngle(islandType, side, anchor.axis.y);

	const newIsland = newEmptyIsland(kitchen, islandType as EmptyIslandDefaults, newAngle);

	// left/right butt side panel to side panel; parallel mates the two FRONT faces, exactly as buildGalley does
	const sourcePanel = side === 'parallel' ? newIsland.frontPanel : (side === 'right' ? newIsland.frontPanel.getLeftPanel() : newIsland.frontPanel.getRightPanel());
	const targetPanel = side === 'parallel' ? anchor.frontPanel : (side === 'right' ? anchor.frontPanel.getRightPanel() : anchor.frontPanel.getLeftPanel());

	if (!sourcePanel || !targetPanel) {
		islands.delete(newIsland.id);
		return { success: false, error: `Could not resolve side panels for connection (new island ${newIsland.id}, anchor ${anchor.id}).` };
	}

	// padding is omitted for left/right so connectIslands keeps its 1" default, the correct butt joint;
	// a walkway is only ever opened up for the facing pair
	connectIslands({ sourcePanel, targetPanel, ...(side === 'parallel' ? { padding: GALLEY_WALKWAY_PADDING } : {}) });

	islands.publish('islands', 'added', { ref: newIsland.id, old: null, new: [] });

	return {
		success: true,
		message: side === 'parallel'
			? `Added '${islandType}' island facing island '${anchor.id}' across a ${GALLEY_WALKWAY_PADDING}-inch walkway (axis ${newAngle}deg).`
			: `Added '${islandType}' island (axis ${newAngle}deg) to the ${side} of island '${anchor.id}' (axis ${anchor.axis.y}deg).`,
		data: { islandId: newIsland.id }
	};
};
